import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchBodyPartsApi,
  fetchEquipmentApi,
  fetchExercisesApi,
  fetchMuscleGroupsApi,
  fetchSassTokenApi,
  filterExercisesApi,
  updateExerciseFavoriteApi,
  upsertExerciseApi,
} from "../../api/toolboxExerciseApi";
import * as Endpoints from "../../entities/Endpoints";

// Fetch Exercises with Pagination
export const fetchExercises = createAsyncThunk(
  "exercises/fetchExercises",
  async ({ coachId, pageNumber, pageSize }, { rejectWithValue }) => {
    try {
      return await fetchExercisesApi({ coachId, pageNumber, pageSize });
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch exercises"
      );
    }
  }
);

// Search and Filter Exercises
export const filterExercises = createAsyncThunk(
  "exercises/filterExercises",
  async (
    { coachId, name, isCustomized, equipment, muscleGroup },
    { rejectWithValue }
  ) => {
    try {
      return await filterExercisesApi({
        coachId,
        name,
        isCustomized,
        equipment,
        muscleGroup,
      });
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to filter exercises"
      );
    }
  }
);

export const upsertExercise = createAsyncThunk(
  "exercises/upsertExercise",
  async ({ coachId, exerciseData }, { rejectWithValue }) => {
    try {
      return await upsertExerciseApi(coachId, exerciseData);
    } catch (error) {
      return rejectWithValue(error.data || "Failed to save exercise");
    }
  }
);

export const updateExerciseFavorite = createAsyncThunk(
  "exercises/updateExerciseFavorite",
  async ({ exerciseId, isFavourite }, { rejectWithValue }) => {
    try {
      return await updateExerciseFavoriteApi(exerciseId, isFavourite);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update favorite status"
      );
    }
  }
);

// Fetch Body Parts
export const fetchBodyParts = createAsyncThunk(
  "exercises/fetchBodyParts",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchBodyPartsApi();
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch body parts"
      );
    }
  }
);

// Fetch Equipment
export const fetchEquipment = createAsyncThunk(
  "exercises/fetchEquipment",
  async ({ muscleId }, { rejectWithValue }) => {
    try {
      return await fetchEquipmentApi({ muscleId });
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch equipment"
      );
    }
  }
);

// Fetch Muscle Groups
export const fetchMuscleGroups = createAsyncThunk(
  "exercises/fetchMuscleGroups",
  async ({ equipmentId }, { rejectWithValue }) => {
    try {
      return await fetchMuscleGroupsApi({ equipmentId });
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch muscle groups"
      );
    }
  }
);

// Fetch SAS Token
export const fetchSassToken = createAsyncThunk(
  `${Endpoints.EXERCISE_GET_SASS_TOKEN}`,
  async ({ fileName, isMessenger }, { rejectWithValue }) => {
    try {
      return await fetchSassTokenApi({ fileName, isMessenger });
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch SAS token"
      );
    }
  }
);

// Slice
const exercisesSlice = createSlice({
  name: "exercises",
  initialState: {
    exerciseList: [],
    totalCount: 0,
    loading: false,
    error: null,
    pagination: { current: 1, pageSize: 10 },
    searchQuery: "",
    toastConfig: null,
    bodyParts: [],
    equipment: [],
    muscleGroups: [],
    secondaryMuscleGroups: "",
    sassToken: null,
    sassTokenLoading: false,
    sassTokenError: null,
  },
  reducers: {
    updatePagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    updateSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setToastForExercise: (state, action) => {
      state.toastConfig = action.payload;
    },
    resetToastConfig: (state) => {
      state.toastConfig = null;
    },
    clearSassToken: (state) => {
      state.sassToken = null;
      state.sassTokenError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExercises.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExercises.fulfilled, (state, action) => {
        state.loading = false;
        state.exerciseList = action.payload.exerciseList.map((exercise) => ({
          exerciseId: exercise.exerciseId,
          name: exercise.exerciseName,
          note: exercise.note,
          favorite: exercise.isFavourite,
          hasImage: !!exercise.exerciseImage,
          hasVideo: !!exercise.exerciseVideo,
          blobUrl: exercise.exerciseImage?.blobUrl || null,
          photoName: exercise.exerciseImage?.fileName || "",
          videoURL: exercise.exerciseVideo?.blobUrl || null,
          videoName: exercise.exerciseVideo?.fileName || "",
          photoMediaType: exercise.exerciseImage?.mediaType || "",
          videoMediaType: exercise.exerciseVideo?.mediaType || "",
          customized: exercise.isCustomized,
          bodyParts: exercise.bodyParts,
          equipment: exercise.equipment,
          muscleGroups: exercise.targetMuscle,
          secondaryMuscleGroups: exercise.secondaryMuscle,
          instructions: exercise.instructions,
          blobStorageVideoUrl: exercise.blobStorageVideoUrl,
        }));
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchExercises.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(filterExercises.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(filterExercises.fulfilled, (state, action) => {
        state.loading = false;
        state.exerciseList = action.payload.exerciseList.map((exercise) => ({
          exerciseId: exercise.exerciseId,
          name: exercise.exerciseName,
          note: exercise.note,
          favorite: exercise.isFavourite,
          hasImage: !!exercise.exerciseImage,
          hasVideo: !!exercise.exerciseVideo,
          blobUrl: exercise.exerciseImage?.blobUrl || null,
          photoName: exercise.exerciseImage?.fileName || "",
          videoURL: exercise.exerciseVideo?.url || null,
          videoName: exercise.exerciseVideo?.fileName || "",
          photoMediaType: exercise.exerciseImage?.mediaType || "",
          videoMediaType: exercise.exerciseVideo?.mediaType || "",
          customized: exercise.isCustomized,
          bodyParts: exercise.bodyParts,
          equipment: exercise.equipment,
          muscleGroups: exercise.targetMuscle,
          secondaryMuscleGroups: exercise.secondaryMuscle,
          instructions: exercise.instructions,
        }));
        state.totalCount = action.payload.totalCount;

        state.pagination = {
          ...state.pagination,
          current: 1,
        };
      })
      .addCase(filterExercises.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(upsertExercise.pending, (state) => {
        state.loading = true;
      })
      .addCase(upsertExercise.fulfilled, (state, action) => {
        state.loading = false;
        state.pagination = { ...state.pagination, current: 1 };
        state.toastConfig = {
          type: "success",
          message: action.meta.arg.exerciseData.exerciseId
            ? "Exercise updated successfully!"
            : "Exercise added successfully!",
          heading: "Success",
        };
      })
      .addCase(upsertExercise.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.toastConfig = {
          type: "error",
          message:
            action.payload?.message ||
            "Failed to save exercise. Please try again.",
          heading: "Error",
        };
      })

      .addCase(updateExerciseFavorite.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateExerciseFavorite.fulfilled, (state, action) => {
        state.loading = false;
        const updatedExerciseId = action.meta.arg.exerciseId;
        const isFavourite = action.meta.arg.isFavourite;

        state.exerciseList = state.exerciseList.map((exercise) =>
          exercise.exerciseId === updatedExerciseId
            ? { ...exercise, favorite: isFavourite }
            : exercise
        );

        state.toastConfig = {
          type: "success",
          message: isFavourite
            ? "Added to favorites successfully!"
            : "Removed from favorites!",
          heading: "Success",
        };
      })
      .addCase(updateExerciseFavorite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.toastConfig = {
          type: "error",
          message: "Failed to update favorite status. Please try again.",
          heading: "Error",
        };
      })

      .addCase(fetchBodyParts.fulfilled, (state, action) => {
        state.bodyParts = action.payload.map((bodyParts) => ({
          ...bodyParts,
          name:
            bodyParts.name.charAt(0).toUpperCase() + bodyParts.name.slice(1),
        }));
      })

      .addCase(fetchEquipment.fulfilled, (state, action) => {
        state.equipment = action.payload.map((equipment) => ({
          ...equipment,
          name:
            equipment.name.charAt(0).toUpperCase() + equipment.name.slice(1),
        }));
      })

      .addCase(fetchMuscleGroups.fulfilled, (state, action) => {
        state.muscleGroups = action.payload.map((muscle) => ({
          ...muscle,
          name: muscle.name.charAt(0).toUpperCase() + muscle.name.slice(1),
        }));
      })

      // SAS Token cases
      .addCase(fetchSassToken.pending, (state) => {
        state.sassTokenLoading = true;
        state.sassTokenError = null;
      })
      .addCase(fetchSassToken.fulfilled, (state, action) => {
        state.sassTokenLoading = false;
        state.sassToken = action.payload;
      })
      .addCase(fetchSassToken.rejected, (state, action) => {
        state.sassTokenLoading = false;
        state.sassTokenError = action.payload;
      });
  },
});

export const {
  updatePagination,
  updateSearchQuery,
  setToastForExercise,
  resetToastConfig,
  clearSassToken,
} = exercisesSlice.actions;

export default exercisesSlice.reducer;
